import styled, { css, keyframes } from 'styled-components';
import { down } from 'styled-breakpoints';

// ── Layout ──────────────────────────────────────────────

export const PageWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    gap: 0;
    margin: -${theme.spacing[6]};
    min-height: calc(100vh - ${theme.layout.header});

    ${down('lg')} {
      margin: -${theme.spacing[4]};
    }

    ${down('md')} {
      margin: -${theme.spacing[3]};
    }
  `}
`;

export const MainContent = styled.main`
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  padding: 1.75rem 2rem;

  ${down('sm')} {
    padding: 1.25rem 1rem;
  }
`;

// ── Page Header ─────────────────────────────────────────

export const PageHeader = styled.header`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1.5rem;
    flex-wrap: wrap;
    gap: 0.75rem;

    h1 {
      font-size: 1.375rem;
      font-weight: 800;
      color: ${theme.colors.foreground[100]};
      letter-spacing: -0.5px;
    }
  `}
`;

export const PageHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

export const BackButton = styled.button`
  ${({ theme }) => css`
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 0.75rem;
    border: none;
    background: transparent;
    cursor: pointer;
    color: ${theme.colors.brand.primary.default};
    display: flex;
    align-items: center;
    justify-content: center;
    transition: ${theme.transition.default};

    :hover {
      background: ${theme.colors.brand.primary.opaque};
    }
  `}
`;

export const CountBadge = styled.span`
  ${({ theme }) => css`
    border-radius: 9999px;
    background: ${theme.colors.brand.primary.opaque};
    color: ${theme.colors.brand.primary.default};
    font-size: 0.75rem;
    font-weight: 700;
    padding: 0.25rem 0.75rem;
  `}
`;

// ── Search & Filters ────────────────────────────────────

export const SearchFiltersRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;

  ${down('sm')} {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const FilterPills = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  flex: 1;
`;

type FilterPillProps = { $active?: boolean };

export const FilterPill = styled.button<FilterPillProps>`
  ${({ theme, $active }) => css`
    padding: 0.375rem 0.875rem;
    border-radius: 1.25rem;
    font-size: 0.75rem;
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.2s ease;
    border: 1px solid
      ${$active
        ? theme.colors.brand.primary.default
        : theme.colors.background[40]};
    background: ${$active
      ? theme.colors.brand.primary.default
      : theme.colors.background[100]};
    color: ${$active
      ? theme.colors.brand.primary.text
      : theme.colors.foreground[80]};

    &:hover {
      ${!$active &&
      css`
        border-color: ${theme.colors.brand.primary.default};
        color: ${theme.colors.brand.primary.default};
      `}
    }
  `}
`;

export const RoomDropdown = styled.div`
  ${({ theme }) => css`
    width: 16rem;

    .room-select-container {
      width: 100%;
    }

    .room-select__custom-value {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      position: absolute;
      left: 0.75rem;
    }

    .room-value-icon {
      color: ${theme.colors.foreground[40]};
      flex-shrink: 0;
    }

    .room-value-label {
      font-size: 0.8125rem;
      font-weight: 500;
      color: ${theme.colors.foreground[40]};
    }

    .room-value-name {
      font-size: 0.8125rem;
      font-weight: 700;
      color: ${theme.colors.foreground[100]};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 8rem;
    }

    .room-select__control {
      background: ${theme.colors.background[100]};
      border: 1px solid ${theme.colors.background[40]};
      border-radius: 0.5rem;
      min-height: 2.75rem;
      cursor: pointer;
      box-shadow: none;

      &:hover {
        border-color: ${theme.colors.brand.primary.default};
      }
    }

    .room-select__value-container {
      padding: 0 0.75rem;
    }

    .room-select__single-value {
      margin: 0;
    }

    .room-select__indicator-separator {
      display: none;
    }

    .room-select__dropdown-indicator {
      color: ${theme.colors.foreground[40]};
      padding: 0 0.75rem;
    }

    .room-select__menu {
      background: ${theme.colors.background[100]};
      border: 1px solid ${theme.colors.background[40]};
      border-radius: 0.5rem;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
      z-index: 20;
      overflow: hidden;
      margin-top: 0.25rem;
    }

    .room-select__menu-list {
      padding: 0.25rem 0;
    }

    .room-select__option {
      font-size: 0.8125rem;
      padding: 0.625rem 0.75rem;
      cursor: pointer;
      color: ${theme.colors.foreground[80]};
      background: transparent;
    }

    .room-select__option:hover {
      background: ${theme.colors.background[80]};
      color: ${theme.colors.brand.primary.default};
    }

    .room-select__option--is-selected {
      background: ${theme.colors.brand.primary.opaque};
      color: ${theme.colors.brand.primary.default};
      font-weight: 700;
    }

    .room-select__option--is-focused {
      background: ${theme.colors.background[80]};
    }
  `}
`;

// ── Speaker Grid ────────────────────────────────────────

export const SpeakerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-left: 2.5rem;

  ${down('lg')} {
    grid-template-columns: repeat(2, 1fr);
  }

  ${down('sm')} {
    grid-template-columns: 1fr;
  }
`;

// ── Speaker Card ────────────────────────────────────────

export const SpeakerCard = styled.div`
  ${({ theme }) => css`
    background: ${theme.colors.background[100]};
    border-radius: 1rem;
    border: 1px solid ${theme.colors.background[40]};
    overflow: hidden;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
    }
  `}
`;

export const CardPhotoArea = styled.div`
  width: 100%;
  aspect-ratio: 1;
  overflow: hidden;

  > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 0.4s ease;
  }

  ${SpeakerCard}:hover > & > img {
    transform: scale(1.03);
  }
`;

export const CardAvatar = styled.img``;

export const CardAvatarFallback = styled.div`
  ${({ theme }) => css`
    width: 100%;
    height: 100%;
    background: linear-gradient(
      135deg,
      ${theme.colors.brand.primary.opaque},
      ${theme.colors.brand.secondary.opaque}
    );
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    font-weight: 700;
    color: ${theme.colors.brand.primary.default};
  `}
`;

export const OverlayCTA = styled.span`
  ${({ theme }) => css`
    position: absolute;
    top: 0.75rem;
    left: 0.75rem;
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.3px;
    color: ${theme.colors.brand.primary.text};
    background: ${theme.colors.brand.primary.default};
    padding: 0.375rem 0.875rem;
    border-radius: 1.25rem;
    opacity: 0;
    transform: translateY(-4px);
    transition: all 0.25s ease;

    ${SpeakerCard}:hover & {
      opacity: 1;
      transform: translateY(0);
    }
  `}
`;

export const CardInfo = styled.div`
  padding: 1rem 1.125rem;
  text-align: center;
`;

export const CompanyLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 2rem;
  margin-top: 0.5rem;

  > img {
    max-height: 100%;
    max-width: 100%;
    object-fit: contain;
  }
`;

export const CardName = styled.strong`
  ${({ theme }) => css`
    display: block;
    font-size: 0.9375rem;
    font-weight: 700;
    color: ${theme.colors.foreground[100]};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `}
`;

export const CardTitle = styled.span`
  display: block;
  font-size: 0.75rem;
  color: var(--colors-foreground-60);
  margin-bottom: 0.5rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const CardStage = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.6875rem;
    color: ${theme.colors.foreground[40]};
    font-weight: 500;

    > svg {
      flex-shrink: 0;
    }
  `}
`;

// ── Empty State ─────────────────────────────────────────

export const EmptyState = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
    text-align: center;
    grid-column: 1 / -1;

    > svg {
      color: ${theme.colors.foreground[40]};
      margin-bottom: 1rem;
    }

    > h3 {
      font-size: 1rem;
      font-weight: 600;
      color: ${theme.colors.foreground[80]};
      margin-bottom: 0.375rem;
    }

    > p {
      font-size: 0.8125rem;
      color: ${theme.colors.foreground[40]};
      margin-bottom: 1.25rem;
    }
  `}
`;

export const ClearFiltersButton = styled.button`
  ${({ theme }) => css`
    background: ${theme.colors.brand.primary.default};
    color: ${theme.colors.brand.primary.text};
    border: none;
    border-radius: 0.5rem;
    padding: 0.5rem 1.25rem;
    font-size: 0.8125rem;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.9;
    }
  `}
`;

// ── Sidebar ─────────────────────────────────────────────

export const ScheduleWrapper = styled.div`
  flex: 1;
  min-height: 0;
  position: relative;
  overflow: hidden;
`;

export const ScheduleFade = styled.div`
  ${({ theme }) => css`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 3rem;
    background: linear-gradient(transparent, ${theme.colors.background[100]});
    pointer-events: none;
  `}
`;

export const Sidebar = styled.aside`
  ${({ theme }) => css`
    width: 420px;
    flex-shrink: 0;
    border-left: 1px solid ${theme.colors.background[40]};
    background: ${theme.colors.background[100]};
    position: sticky;
    top: ${theme.layout.header};
    height: calc(100vh - ${theme.layout.header});
    overflow: hidden;
    padding: 1.25rem;
    display: flex;
    flex-direction: column;

    &::-webkit-scrollbar {
      width: 4px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background: ${theme.colors.background[40]};
      border-radius: 4px;
    }

    ${down('xl')} {
      display: none;
    }
  `}
`;

export const SidebarSectionTitle = styled.h3`
  ${({ theme }) => css`
    font-size: 0.875rem;
    font-weight: 700;
    color: ${theme.colors.foreground[100]};
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  `}
`;

// ── Live Now ────────────────────────────────────────────

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

export const LiveNowSection = styled.section`
  margin-bottom: 2rem;
`;

export const LiveBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #ef4444;
`;

export const PulseDot = styled.span`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: #ef4444;
  animation: ${pulse} 1.5s ease-in-out infinite;
`;

export const LiveCard = styled.div`
  ${({ theme }) => css`
    background: ${theme.colors.background[80]};
    border-radius: 0.75rem;
    padding: 1rem;
    margin-top: 0.75rem;
  `}
`;

export const LiveCardTitle = styled.div`
  ${({ theme }) => css`
    font-size: 0.875rem;
    font-weight: 600;
    color: ${theme.colors.foreground[100]};
    margin-bottom: 0.25rem;
  `}
`;

export const LiveCardSpeaker = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;

  > img {
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 0.5rem;
    object-fit: cover;
  }

  > span {
    font-size: 0.75rem;
    color: var(--colors-foreground-60);
  }
`;

export const WatchButton = styled.button`
  ${({ theme }) => css`
    width: 100%;
    padding: 0.5rem;
    border: none;
    border-radius: 0.5rem;
    background: ${theme.colors.brand.primary.default};
    color: ${theme.colors.brand.primary.text};
    font-size: 0.75rem;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.9;
    }
  `}
`;

// ── Next Up (when not live) ──────────────────────────────

export const NextUpSection = styled.section`
  margin-bottom: 2rem;
`;

export const NextUpBadge = styled.span`
  ${({ theme }) => css`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.6875rem;
    font-weight: 700;
    text-transform: uppercase;
    color: ${theme.colors.brand.primary.default};
  `}
`;

export const NextUpCard = styled.div`
  ${({ theme }) => css`
    background: ${theme.colors.background[80]};
    border-radius: 0.75rem;
    padding: 1rem;
    margin-top: 0.75rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  `}
`;

export const NextUpTime = styled.div`
  ${({ theme }) => css`
    font-size: 0.6875rem;
    font-weight: 600;
    color: ${theme.colors.foreground[40]};
  `}
`;

export const NextUpTitle = styled.div`
  ${({ theme }) => css`
    font-size: 0.875rem;
    font-weight: 600;
    color: ${theme.colors.foreground[100]};
  `}
`;

export const NextUpSpeaker = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  > img {
    width: 2rem;
    height: 2rem;
    border-radius: 0.5rem;
    object-fit: cover;
  }

  > span {
    font-size: 0.75rem;
    color: var(--colors-foreground-60);
  }
`;

// ── Skeleton ────────────────────────────────────────────

export const SkeletonCard = styled.div`
  ${({ theme }) => css`
    background: ${theme.colors.background[100]};
    border-radius: 1rem;
    border: 1px solid ${theme.colors.background[40]};
    overflow: hidden;
  `}
`;

export const SkeletonPhotoArea = styled.div`
  height: 12rem;
`;

export const SkeletonInfo = styled.div`
  padding: 1rem 1.125rem;
`;
